import type { ReactNode } from 'react';

import { lazy } from '@infinex-private/ts-utils/function';
import { base64Encode } from '@infinex-private/ts-utils/string';
import { colors } from '@infinex/ui-constants';
import {
  ConfidentialFillIcon,
  CrownIcon,
  DepositFillIcon,
  DollarCircleFillIcon,
  EncryptedFillIcon,
  InfinexIcon,
  InfinexSaleFilledIcon,
  MoveFillIcon,
  TrophyIcon,
  WithdrawIcon,
} from '@infinex/ui-icons';

export const FALLBACK_DATA_URI = lazy(() => {
  const nft = base64Encode(
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
      <path fill="${colors.background.fill}" d="M0 0h24v24H0z"/>
      <path fill="${colors.fill.placeholder}" fill-rule="evenodd" d="M11 20c.62.33 1.38.33 2 0l5.87-3.13c.7-.37 1.13-1.1 1.13-1.88V9c0-.78-.43-1.5-1.13-1.88L13 4a2.13 2.13 0 0 0-2 0L5.13 7.13C4.43 7.5 4 8.23 4 9.01V15c0 .78.43 1.5 1.13 1.88L11 20Zm.47-4.42c.1.6.97.6 1.06 0l.05-.36a5.33 5.33 0 0 1 2.27-3.62l.45-.3c.54-.37.07-1.2-.52-.93l-.56.26a5.33 5.33 0 0 1-4.44 0l-.56-.26c-.6-.27-1.06.56-.52.93l.45.3a5.33 5.33 0 0 1 2.27 3.62l.05.36Z" clip-rule="evenodd"/>
    </svg>`,
    'image/svg+xml'
  );

  return {
    nft,
    'nft-collection': nft,
    token: base64Encode(
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
        <path fill="${colors.background.fill}" d="M0 0h24v24H0z"/>
        <path fill="${colors.fill.placeholder}" fill-rule="evenodd" d="M20 10.29v3.42c0 2.53-3.58 4.58-8 4.58s-8-2.05-8-4.58V10.3C4 7.76 7.58 5.7 12 5.7s8 2.05 8 4.58ZM7.29 12.57a8 8 0 0 0 9.42 0c.38-.27.12-.88-.34-.8l-2.73.5c-1.09.2-2.2.2-3.28 0l-2.73-.5c-.46-.08-.72.53-.34.8Z" clip-rule="evenodd"/>
      </svg>`,
      'image/svg+xml'
    ),
  };
});

export const SWIDGE_CLIP_ID = 'emblem-swidge-clip';
export const SWIDGE_TO_DIAMETER_RATIO = 0.85;
export const SWIDGE_FROM_DIAMETER_RATIO = 0.5;

export type EmblemSize = keyof typeof EMBLEM_RECT;

const EMBLEM_RECT = {
  xsmall: { size: 20, radius: 4 },
  small: { size: 24, radius: 6 },
  medium: { size: 32, radius: 8 },
  large: { size: 40, radius: 12 },
  xlarge: { size: 56, radius: 16 },
};

export const EMBLEM_DIMENSIONS = {
  xsmall: {
    ...EMBLEM_RECT.xsmall,
    activityRect: {
      top: -2,
      left: -2,
      height: 8,
      width: 8,
      stroke: 1,
      radius: 2,
    },
    activityClipId: 'emblem-activity-clip-xsmall',
    chainRect: {
      top: 10,
      left: 10,
      height: 8,
      width: 8,
      stroke: 1,
      radius: 2,
    },
    chainClipId: 'emblem-chain-clip-xsmall',
    chainGroupClipId: getChainEmblemClipId('xsmall'),
    chainGroupRect: getChainEmblemMaskRect('xsmall'),
  },
  small: {
    ...EMBLEM_RECT.small,
    activityRect: {
      top: -2,
      left: -2,
      height: 12,
      width: 12,
      stroke: 2,
      radius: 3,
    },
    activityClipId: 'emblem-activity-clip-small',
    chainRect: {
      top: 14,
      left: 14,
      height: 12,
      width: 12,
      stroke: 2,
      radius: 3,
    },
    chainClipId: 'emblem-chain-clip-small',
    chainGroupClipId: getChainEmblemClipId('small'),
    chainGroupRect: getChainEmblemMaskRect('small'),
  },
  medium: {
    ...EMBLEM_RECT.medium,
    activityRect: {
      top: -3,
      left: -3,
      height: 16,
      width: 16,
      stroke: 2,
      radius: 4,
    },
    activityClipId: 'emblem-activity-clip-medium',
    chainRect: {
      top: 19,
      left: 19,
      height: 16,
      width: 16,
      stroke: 2,
      radius: 4,
    },
    chainClipId: 'emblem-chain-clip-medium',
    chainGroupClipId: getChainEmblemClipId('medium'),
    chainGroupRect: getChainEmblemMaskRect('medium'),
  },
  large: {
    ...EMBLEM_RECT.large,
    activityRect: {
      top: -4,
      left: -4,
      height: 20,
      width: 20,
      stroke: 2,
      radius: 6,
    },
    activityClipId: 'emblem-activity-clip-large',
    chainRect: {
      top: 24,
      left: 24,
      height: 20,
      width: 20,
      stroke: 2,
      radius: 6,
    },
    chainClipId: 'emblem-chain-clip-large',
    chainGroupClipId: getChainEmblemClipId('large'),
    chainGroupRect: getChainEmblemMaskRect('large'),
  },
  xlarge: {
    ...EMBLEM_RECT.xlarge,
    activityRect: {
      top: -6,
      left: -6,
      height: 28,
      width: 28,
      stroke: 3,
      radius: 8,
    },
    activityClipId: 'emblem-activity-clip-xlarge',
    chainRect: {
      top: 34,
      left: 34,
      height: 28,
      width: 28,
      stroke: 3,
      radius: 8,
    },
    chainClipId: 'emblem-chain-clip-xlarge',
    chainGroupClipId: getChainEmblemClipId('xlarge'),
    chainGroupRect: getChainEmblemMaskRect('xlarge'),
  },
};

function getChainEmblemMaskRect(size: EmblemSize) {
  const rect = EMBLEM_RECT[size];
  const overlap = Math.min(rect.radius, 6);
  const offset = overlap / 2;

  return {
    overlap,
    gap: Math.min(rect.radius, 8),
    left: (rect.size - overlap - offset / 2) * -1,
    top: (offset / 2) * -1,
    height: rect.size + offset,
    width: rect.size + offset,
    radius: rect.radius,
  };
}
function getChainEmblemClipId(size: EmblemSize) {
  return `emblem-chain-group-clip-${size}`;
}

/**
 * Wraps a `@infinex/ui-icons` icon so its coverage inside the badge matches
 * the designed glyphs, which are drawn edge-to-edge in a 20-box with their
 * padding built in. Used only for activity kinds without a designed badge.
 */
function InsetIconGlyph({ children }: { children: ReactNode }) {
  return (
    <span className="flex size-full items-center justify-center p-[8%]">
      {children}
    </span>
  );
}

// Designed glyph paths are taken verbatim from the Figma activity badge
// components (20-box, padding built in). Undesigned kinds inset a ui-icon.
export const ACTIVITY_METADATA = {
  airdrop: {
    name: 'Airdrop',
    glyph: (
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.8117 3.23855C8.07746 2.23795 4.2391 4.45403 3.23851 8.18829C3.12576 8.60909 3.36354 8.99331 3.73417 9.15913C3.79052 9.18435 3.84217 9.22032 3.87875 9.27006L6.99133 13.5011L6.51522 15.2779C6.37228 15.8114 6.68886 16.3597 7.22233 16.5027L9.15418 17.0203C9.68765 17.1633 10.236 16.8467 10.3789 16.3132L10.855 14.5364L15.6661 12.4285C15.7227 12.4037 15.7854 12.3984 15.8468 12.4047C16.2507 12.4464 16.6487 12.2326 16.7615 11.8118C17.7621 8.0775 15.546 4.23914 11.8117 3.23855ZM9.19761 10.0977C9.23159 9.97086 9.17936 9.83614 9.06666 9.76881C8.07617 9.17704 7.05781 8.84595 5.98325 8.8687C5.75413 8.87355 5.64049 9.13354 5.77629 9.31814L8.14384 12.5364C8.18709 12.5952 8.25041 12.6358 8.32094 12.6546C8.38055 12.6705 8.43986 12.6878 8.49931 12.7038L9.19761 10.0977ZM14.1036 11.4754C14.3106 11.3847 14.3445 11.1088 14.1509 10.9922C13.3315 10.4986 12.2723 10.269 10.9222 10.285C10.7899 10.2865 10.6756 10.3772 10.6414 10.5051L9.9482 13.092C10.0439 13.1176 10.1411 13.14 10.2355 13.1701L14.1036 11.4754Z"
        />
      </svg>
    ),
  },
  cashback: {
    name: 'Cashback',
    glyph: (
      <InsetIconGlyph>
        <DollarCircleFillIcon />
      </InsetIconGlyph>
    ),
  },
  deposit: {
    name: 'Deposit',
    glyph: (
      <InsetIconGlyph>
        <DepositFillIcon />
      </InsetIconGlyph>
    ),
  },
  gas: {
    name: 'Gas',
    glyph: (
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.99996 2.66663C14.05 2.66663 17.3333 5.94987 17.3333 9.99996C17.3333 14.05 14.05 17.3333 9.99996 17.3333C5.94987 17.3333 2.66663 14.05 2.66663 9.99996C2.66663 5.94987 5.94987 2.66663 9.99996 2.66663ZM10.9791 5.41077C10.7036 5.26476 10.3637 5.32965 10.1608 5.56637L6.16077 10.233C5.99133 10.4307 5.95237 10.7087 6.06116 10.9453C6.16998 11.1817 6.40633 11.3333 6.66663 11.3333H9.3691L8.69006 13.8248C8.60818 14.1256 8.74545 14.4431 9.02079 14.5892C9.2963 14.7351 9.63624 14.6703 9.83915 14.4336L13.8392 9.76689C14.0086 9.56922 14.0475 9.29116 13.9388 9.05465C13.83 8.81821 13.5936 8.66665 13.3333 8.66663H10.6308L11.3099 6.17509C11.3917 5.87439 11.2544 5.55681 10.9791 5.41077Z" />
      </svg>
    ),
  },
  inx: {
    name: 'INX',
    glyph: (
      <InsetIconGlyph>
        <InfinexIcon />
      </InsetIconGlyph>
    ),
  },
  move: {
    name: 'Move',
    glyph: (
      <InsetIconGlyph>
        <MoveFillIcon />
      </InsetIconGlyph>
    ),
  },
  patron: {
    name: 'Patron',
    glyph: (
      <InsetIconGlyph>
        <CrownIcon />
      </InsetIconGlyph>
    ),
  },
  'private-receive': {
    name: 'Private receive',
    glyph: (
      <InsetIconGlyph>
        <ConfidentialFillIcon />
      </InsetIconGlyph>
    ),
  },
  'private-send': {
    name: 'Private send',
    glyph: (
      <InsetIconGlyph>
        <EncryptedFillIcon />
      </InsetIconGlyph>
    ),
  },
  'prize-draw': {
    name: 'Prize draw',
    glyph: (
      <InsetIconGlyph>
        <TrophyIcon />
      </InsetIconGlyph>
    ),
  },
  receive: {
    name: 'Receive',
    glyph: (
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.11662 12.2651V5.22965C9.11662 4.98431 9.20251 4.77576 9.37429 4.60398C9.54596 4.4322 9.75451 4.34631 9.99996 4.34631C10.2454 4.34631 10.454 4.4322 10.6256 4.60398C10.7974 4.77576 10.8833 4.98431 10.8833 5.22965V12.2651L13.7826 9.36598C13.9598 9.18865 14.1688 9.1012 14.4095 9.10365C14.65 9.10598 14.8589 9.19581 15.0361 9.37315C15.2023 9.55048 15.2867 9.75942 15.2891 9.99998C15.2916 10.2405 15.2041 10.4495 15.0268 10.6268L10.6268 15.0268C10.5412 15.1123 10.4439 15.1768 10.3348 15.2203C10.2256 15.2638 10.114 15.2855 9.99996 15.2855C9.88596 15.2855 9.77434 15.2638 9.66512 15.2203C9.55601 15.1768 9.45868 15.1123 9.37312 15.0268L4.96379 10.6173C4.79123 10.4449 4.70496 10.2387 4.70496 9.99881C4.70496 9.75904 4.79123 9.55048 4.96379 9.37315C5.14101 9.19581 5.35112 9.10715 5.59412 9.10715C5.83712 9.10715 6.04729 9.19581 6.22462 9.37315L9.11662 12.2651Z" />
      </svg>
    ),
  },
  redemption: {
    name: 'Redemption',
    glyph: (
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path d="M5.36 5.36a.9.9 0 0 1 1.28 0l4 4a.9.9 0 0 1 0 1.28l-4 4a.9.9 0 0 1-1.28-1.28L8.73 10 5.36 6.64a.9.9 0 0 1 0-1.28Zm5 0a.9.9 0 0 1 1.28 0l4 4a.9.9 0 0 1 0 1.28l-4 4a.9.9 0 0 1-1.28-1.28L13.73 10l-3.37-3.36a.9.9 0 0 1 0-1.28Z" />
      </svg>
    ),
  },
  sale: {
    name: 'Infinex Sale',
    glyph: (
      <InsetIconGlyph>
        <InfinexSaleFilledIcon />
      </InsetIconGlyph>
    ),
  },
  send: {
    name: 'Send',
    glyph: (
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path d="M12.8687 15.98L16.6154 4.64736C16.874 3.86736 16.1327 3.12603 15.3527 3.38403L4.02004 7.13136C3.13204 7.4247 3.09737 8.66936 3.96871 9.01136L6.40737 9.96936C6.69507 10.0824 7.00504 10.1272 7.31296 10.1002C7.62087 10.0732 7.91835 9.97529 8.18204 9.81403L10.9807 8.1027C11.578 7.73736 12.262 8.4227 11.8974 9.01936L10.186 11.818C10.0248 12.0817 9.92682 12.3792 9.89987 12.6871C9.87292 12.995 9.9177 13.305 10.0307 13.5927L10.9887 16.0314C11.3307 16.9027 12.5754 16.868 12.8687 15.98Z" />
      </svg>
    ),
  },
  swidge: {
    name: 'Swidge',
    glyph: (
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path d="M7.00871 13.7262C6.49816 14.159 5.96027 14.3556 5.39505 14.316C4.82982 14.2764 4.33788 14.0853 3.91921 13.7429C3.50066 13.4003 3.21249 12.9464 3.05471 12.3812C2.89705 11.816 2.99816 11.2384 3.35805 10.6485L4.55155 8.67619C4.28632 8.41285 4.07471 8.10269 3.91671 7.74569C3.75871 7.38869 3.67971 7.00702 3.67971 6.60069C3.67971 5.79202 3.96438 5.10302 4.53371 4.53369C5.10305 3.96435 5.79205 3.67969 6.60071 3.67969C7.40949 3.67969 8.09855 3.96435 8.66788 4.53369C9.2371 5.10302 9.52171 5.79202 9.52171 6.60069C9.52171 7.40946 9.2371 8.09852 8.66788 8.66785C8.09855 9.23708 7.40949 9.52169 6.60071 9.52169C6.51694 9.52169 6.4331 9.51774 6.34921 9.50985C6.26532 9.50185 6.18616 9.48991 6.11171 9.47402L4.87538 11.5652C4.77827 11.7213 4.7471 11.8764 4.78188 12.0305C4.81666 12.1846 4.89049 12.3101 5.00338 12.407C5.11638 12.5039 5.2511 12.559 5.40755 12.5722C5.56388 12.5854 5.71449 12.53 5.85938 12.4059L13.0007 6.26669C13.5239 5.82124 14.0709 5.62113 14.6417 5.66635C15.2125 5.71146 15.7056 5.90841 16.121 6.25719C16.5365 6.60608 16.8156 7.06152 16.9584 7.62352C17.1012 8.18563 16.9957 8.76163 16.642 9.35152L15.458 11.3334C15.7233 11.5967 15.9333 11.9045 16.088 12.2569C16.2429 12.6093 16.3204 12.9901 16.3204 13.3994C16.3204 14.208 16.0357 14.897 15.4664 15.4664C14.897 16.0357 14.208 16.3204 13.3994 16.3204C12.5906 16.3204 11.9015 16.0357 11.3322 15.4664C10.763 14.897 10.4784 14.208 10.4784 13.3994C10.4784 12.5906 10.763 11.9015 11.3322 11.3322C11.9015 10.763 12.5906 10.4784 13.3994 10.4784C13.4844 10.4784 13.567 10.4823 13.6474 10.4902C13.7277 10.4982 13.8057 10.5101 13.8812 10.526L15.134 8.44419C15.2312 8.28819 15.259 8.13335 15.2177 7.97969C15.1765 7.82613 15.0993 7.7008 14.9862 7.60369C14.8732 7.50658 14.7387 7.4528 14.5827 7.44235C14.4266 7.43202 14.2793 7.4858 14.1407 7.60369L7.00871 13.7262Z" />
      </svg>
    ),
  },
  withdraw: {
    name: 'Withdraw',
    glyph: (
      <InsetIconGlyph>
        <WithdrawIcon />
      </InsetIconGlyph>
    ),
  },
};
